import { throttle } from 'lodash-es';
import { memo, useCallback, useMemo, useEffect } from 'react';
import type { Vector3 } from 'three';
import { getAllDevices } from '@ubnt/link-core/modules/devices';
import { useDispatch, useSelector } from 'app/store';
import { activeElementLinkIdSelector } from 'modules/activeElement/externalSelectors';
import { devicesDeviceIdsSelector, devicesDeviceUpdate } from 'modules/devices/slice';
import {
  linksLinkApIdGetter,
  linksLinkByIdSelector,
  linksLinkClientIdGetter,
  linksLinkIdsSelector,
} from 'modules/links/slice';
import { captureException } from 'utils/captureException';
import { getSingleImgSrc } from 'utils/imageUtils';
import { useDeviceImage } from 'modules/editor/hooks/useDeviceTexture';
import { useImageSupport } from 'hooks/useImageSupport';

import { Device } from './Device';
import { Link } from './Link';

function DevicesImpl() {
  const dispatch = useDispatch();

  const deviceIds = useSelector(devicesDeviceIdsSelector);
  const linkIds = useSelector(linksLinkIdsSelector);
  const activeLinkId = useSelector(activeElementLinkIdSelector);
  const activeLink = useSelector((state) => linksLinkByIdSelector(state, activeLinkId));
  const { isLoading, supportsAvif, supportsWebp } = useImageSupport();
  useEffect(() => {
    if (isLoading) {
      return;
    }

    try {
      getAllDevices()
        .filter((device) => device.isWifiDevice() && device.imgSrc)
        .forEach((device) => {
          useDeviceImage.preload(getSingleImgSrc(device.imgSrc, supportsAvif, supportsWebp));
          device.antennas.forEach((antenna) => {
            if (!antenna.builtin) {
              useDeviceImage.preload(getSingleImgSrc(antenna.imgSrc, supportsAvif, supportsWebp));
            }
          });
        });
    } catch (e) {
      captureException(e as Error);
    }
  }, [isLoading, supportsAvif, supportsWebp]);

  const onDrag = useCallback(
    ({ x, y }: Vector3, id: string) => {
      dispatch(devicesDeviceUpdate({ id, changes: { position: { x, y } } }));
    },
    [dispatch]
  );

  const onDragThrottled = useMemo(() => throttle(onDrag, 100, { leading: true, trailing: true }), [onDrag]);

  const onDragEnd = useCallback(
    (position: Vector3, id: string) => {
      onDragThrottled.cancel();
      onDrag(position, id);
    },
    [onDrag, onDragThrottled]
  );

  useEffect(() => () => onDragThrottled.cancel(), [onDragThrottled]);

  return (
    <>
      {linkIds.map((linkId) => (
        <Link key={linkId} id={linkId} isActive={linkId === activeLinkId} />
      ))}
      {deviceIds.map((deviceId) => (
        <Device
          isActive={linksLinkApIdGetter(activeLink) === deviceId || linksLinkClientIdGetter(activeLink) === deviceId}
          key={deviceId}
          id={deviceId}
          onDrag={onDragThrottled}
          onDragEnd={onDragEnd}
        />
      ))}
    </>
  );
}

export const Devices = memo(DevicesImpl);
